import type { DaySummaryItem } from 'features/Schedule/api/schedule.types'
import { HIDDEN_WHEN_SHOW_CANCELED_OFF } from 'features/Schedule/pages/components/utils'

import type { DayResults, MinuteRange, RoomBooking, RoomDay } from './ehrAppointmentV2.types'

const DAY_MINUTES = 24 * 60

type Row = Record<string, unknown>

const asRow = (value: unknown): Row | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Row) : null

const asCode = (value: unknown): string | null =>
  typeof value === 'string' || typeof value === 'number' ? String(value) : null

const asText = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value : null

// /schedule/day sends naive 24-hour site clocks ("09:00:00"); anything else is refused.
const readClock = (value: unknown, date: string): number | null => {
  if (typeof value !== 'string') return null
  const match = value.match(/^(?:(\d{4}-\d{2}-\d{2})[ T])?(\d{2}):(\d{2})(?::\d{2})?$/)
  if (!match || (match[1] && match[1] !== date)) return null
  const minutes = Number(match[2]) * 60 + Number(match[3])
  return minutes < DAY_MINUTES ? minutes : null
}

const readHours = (slots: unknown, date: string): MinuteRange[] =>
  (Array.isArray(slots) ? slots : []).reduce<MinuteRange[]>((hours, slot) => {
    const row = asRow(slot)
    const start = readClock(row?.start_time, date)
    const end = readClock(row?.end_time, date)
    if (start === null || end === null) return hours
    // A slot that ends at 00:00 runs to midnight.
    return [...hours, { start, end: end > start ? end : DAY_MINUTES }]
  }, [])

const readBooking = (item: unknown, date: string): RoomBooking | null => {
  const row = asRow(item)
  const id = row?.id
  const roomCode = asCode(asRow(row?.room)?.code)
  if (typeof id !== 'number' || !roomCode) return null
  if (typeof row?.date === 'string' && row.date !== date) return null

  const start = readClock(row?.start_time, date)
  const end = readClock(row?.end_time, date)
  if (start === null || end === null || end <= start) return null

  const status = asRow(row?.status)?.code
  const patient = asRow(row?.patient)

  return {
    id,
    roomCode,
    start,
    end,
    statusCode: typeof status === 'number' ? status : null,
    patientFirstName: asText(patient?.first_name),
    patientLastName: asText(patient?.last_name),
    appointmentWith: asText(row?.consultation_staff_name),
  }
}

// Validates one /schedule/day answer at the boundary; unreadable rows are dropped, never guessed.
export const toRoomDay = (date: string, payload: unknown): RoomDay => {
  const body = asRow(payload)
  const rooms = Array.isArray(body?.rooms) ? body.rooms : []
  const items = Array.isArray(body?.items) ? body.items : []

  return {
    date,
    rooms: rooms.reduce<RoomDay['rooms']>((found, room) => {
      const row = asRow(room)
      const code = asCode(row?.code)
      return code ? [...found, { code, hours: readHours(row?.time_slots, date) }] : found
    }, []),
    bookings: items.reduce<RoomBooking[]>((found, item) => {
      const booking = readBooking(item, date)
      return booking ? [...found, booking] : found
    }, []),
  }
}

// Canceled and rescheduled visits leave the room free, as the calendar shows them.
const holdsRoom = (booking: RoomBooking, roomCode: string, editedId?: number): boolean =>
  booking.roomCode === roomCode &&
  booking.id !== editedId &&
  !HIDDEN_WHEN_SHOW_CANCELED_OFF.includes(booking.statusCode ?? -1)

const overlaps = (left: MinuteRange, right: MinuteRange): boolean =>
  left.start < right.end && right.start < left.end

const isInsideHours = (slot: MinuteRange, hours: readonly MinuteRange[]): boolean => {
  // Leased hours come as back-to-back blocks; walk them from the slot start.
  let reached = slot.start
  const sorted = [...hours].sort((left, right) => left.start - right.start)
  for (const block of sorted) {
    if (block.start > reached) break
    if (block.end > reached) reached = block.end
    if (reached >= slot.end) return true
  }
  return false
}

// Keeps only the slots where the room is leased and nobody is booked in it.
export const filterByRoom = (
  days: readonly DayResults[],
  roomDays: readonly RoomDay[],
  roomCode: string,
  editedId?: number,
): DayResults[] =>
  days.reduce<DayResults[]>((kept, day) => {
    const schedule = roomDays.find((item) => item.date === day.day)
    const room = schedule?.rooms.find((item) => item.code === roomCode)
    if (!schedule || !room) return kept

    const taken = schedule.bookings.filter((booking) => holdsRoom(booking, roomCode, editedId))
    const options = day.options.filter(
      (slot) =>
        isInsideHours(slot, room.hours) && !taken.some((booking) => overlaps(slot, booking)),
    )

    return options.length ? [...kept, { ...day, options, total: options.length }] : kept
  }, [])

const clockText = (minutes: number): string =>
  `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`

// The room's live bookings in the day-summary shape the preview already draws.
export const roomBookingsFor = (
  day: RoomDay,
  roomCode: string,
  editedId?: number,
): DaySummaryItem[] =>
  day.bookings
    .filter((booking) => holdsRoom(booking, roomCode, editedId))
    .map((booking) => ({
      start_time: `${day.date} ${clockText(booking.start)}`,
      end_time: `${day.date} ${clockText(booking.end)}`,
      appointment_with: booking.appointmentWith,
      patient_first_name: booking.patientFirstName,
      patient_last_name: booking.patientLastName,
    }))
